import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Button,
    TextField,
    Grid,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Box,
    Card,
    CardContent,
    Divider,
    useTheme,
    useMediaQuery,
    Container,
    Stack
} from '@mui/material';
import { QRFirebaseUtils } from '../../utils/firebase';
import { toast } from 'react-toastify';
import Navbar2 from '../../components/Navbar2';
import PageTitle from '../../components/pagetitle';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import Logo from '../../images/logo-nmf.png';
import { LOGO_BASE64 } from './logoBase64';
import './donation-collection.scss';

const MobileDenominationCard = React.memo(({ denom, index, onCountChange }) => {
    const [localValue, setLocalValue] = useState(String(denom.count || ''));

    useEffect(() => {
        setLocalValue(String(denom.count || ''));
    }, [denom.count]);

    const handleChange = (e) => {
        const raw = e.target.value;
        const sanitized = raw.replace(/\D/g, '');
        setLocalValue(sanitized);
        const asNumber = sanitized === '' ? 0 : parseInt(sanitized, 10);
        onCountChange(index, asNumber);
    };

    const parsed = parseInt(localValue || '0', 10);

    return (
        <Card
            variant="outlined"
            sx={{
                mb: 1,
                backgroundColor: parsed > 0 ? '#f0f8ff' : 'inherit',
                border: parsed > 0 ? '2px solid #4472c4' : '1px solid #e0e0e0'
            }}
        >
            <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                        ₹{denom.value}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                            type="text"
                            value={localValue}
                            onChange={handleChange}
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                style: {
                                    fontSize: '18px',
                                    textAlign: 'center',
                                    padding: '12px'
                                }
                            }}
                            sx={{
                                width: '80px',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: parsed > 0 ? '#4472c4' : '#e0e0e0'
                                    }
                                }
                            }}
                            size="small"
                        />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4472c4', minWidth: '80px', textAlign: 'right' }}>
                            ₹{denom.value * (parsed || 0)}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
});

MobileDenominationCard.displayName = 'MobileDenominationCard';

const DonationCollection = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [showPasswordDialog, setShowPasswordDialog] = useState(true);
    const [loading, setLoading] = useState(false);
    const [qrCodeData, setQrCodeData] = useState(null);

    const COLLECTION_PASSWORD = 'nmf2025';

    const [formData, setFormData] = useState({
        qrUUID: searchParams.get('qr') || '',
        boxId: searchParams.get('box') || '',
        location: searchParams.get('location') || '',
        purpose: searchParams.get('purpose') || 'Sadaqah',
        volunteerName: '',
        donorName: '',
        donorPan: '',
        donorEmail: '',
        donorMobile: '',
        donorAddress: '',
        paymentMethod: 'Cash'
    });

    const [denominations, setDenominations] = useState([
        { value: 1, count: 0 },
        { value: 2, count: 0 },
        { value: 5, count: 0 },
        { value: 10, count: 0 },
        { value: 20, count: 0 },
        { value: 50, count: 0 },
        { value: 100, count: 0 },
        { value: 200, count: 0 },
        { value: 500, count: 0 }
    ]);

    const volunteers = [
        'Abdul Rahman',
        'Shaheer Ahmed',
        'Mohammad Ali',
        'Fatima Khan',
        'Aisha Begum',
        'Mohammed Dawood Pasha',
        'Muhammad Farhan Shajeel',
        'Mohammad Sajid Sharfi',
        '⁠Mohammed Ishtiyaq'
    ];

    const organizationDetails = {
        name: 'Nimal Maula Foundation',
        regNo: 'AAGCN5439HE20231',
        corporateId: 'U85300TG2019NPL132903',
        section80G: 'AAGCN5439HF20231',
        darpanId: 'TS/2023/035306',
        address: '16-2-753/1/404, Konark Towers, Andhra Bank Colony, Dilsukhnagar, Hyderabad, Telangana - 500036',
        email: 'talktous@nimalmaulafoundation.org',
        website: 'www.nimalmaulafoundation.org',
        contact: '9000336645'
    };

    const loadQRCodeData = async (uuid) => {
        try {
            setLoading(true);
            const qrData = await QRFirebaseUtils.getQRCodeByUUID(uuid);

            if (!qrData) {
                toast.error('Invalid QR code. Please scan a valid QR code.');
                navigate('/');
                return;
            }

            setFormData(prev => ({
                ...prev,
                qrUUID: uuid,
                boxId: qrData.boxId || '',
                location: qrData.location || '',
                purpose: qrData.purpose || 'Sadaqah',
                donorName: qrData.donorName || '',
                donorPan: qrData.donorPan || '',
                donorEmail: qrData.donorEmail || '',
                donorMobile: qrData.donorMobile || '',
                donorAddress: qrData.donorAddress || ''
            }));

            setQrCodeData(qrData);
            toast.success('QR code data loaded successfully!');
        } catch (error) {
            console.error('Error loading QR code data:', error);
            toast.error('Error loading QR code data. Please try again.');
            navigate('/');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const qrUUID = searchParams.get('qr');
        const boxId = searchParams.get('box');
        if (qrUUID) {
            loadQRCodeData(qrUUID);
        } else if (boxId) {
            setFormData(prev => ({
                ...prev,
                boxId: boxId,
                location: searchParams.get('location') || '',
                purpose: searchParams.get('purpose') || 'Sadaqah'
            }));
        }
    }, [searchParams, navigate]);

    const handlePasswordSubmit = () => {
        if (password === COLLECTION_PASSWORD) {
            setIsAuthenticated(true);
            setShowPasswordDialog(false);
            toast.success('Access granted!');
        } else {
            toast.error('Incorrect password!');
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleDenominationChange = useCallback((index, count) => {
        setDenominations(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], count: Number.isNaN(count) ? 0 : count };
            return updated;
        });
    }, []);

    const calculateTotal = () => {
        return denominations.reduce((total, denom) => total + (denom.value * denom.count), 0);
    };

    const generateReceiptNumber = () => {
        const now = new Date();
        const year = now.getFullYear().toString().slice(-2);
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        return `NMF${month}/${year}-${year + 1}/FSJB-${random}`;
    };

    const saveToFirebase = async (receiptData) => {
        try {
            const docId = await QRFirebaseUtils.saveDonationWithQR(receiptData);
            console.log('Donation saved with ID: ', docId);
            return docId;
        } catch (error) {
            console.error('Error saving donation: ', error);
            throw error;
        }
    };

    const generateReceipt = async () => {
        const total = calculateTotal();

        if (total === 0) {
            toast.error('Please enter denomination amounts.');
            return;
        }

        if (!formData.volunteerName) {
            toast.error('Please select a volunteer name.');
            return;
        }

        if (!formData.donorName) {
            toast.error('Please enter donor name.');
            return;
        }

        const receiptNumber = generateReceiptNumber();
        const now = new Date();

        const receiptData = {
            receiptNumber,
            qrUUID: formData.qrUUID || null,
            boxId: formData.boxId,
            location: formData.location,
            purpose: formData.purpose,
            volunteerName: formData.volunteerName,
            donorName: formData.donorName,
            donorPan: formData.donorPan,
            donorEmail: formData.donorEmail,
            donorMobile: formData.donorMobile,
            donorAddress: formData.donorAddress,
            paymentMethod: formData.paymentMethod,
            denominations: denominations.filter(d => d.count > 0),
            totalAmount: total,
            dateTime: now.toISOString(),
            organizationDetails,
            qrCodeData: qrCodeData ? {
                originalDonorName: qrCodeData.donorName,
                originalDonorMobile: qrCodeData.donorMobile,
                qrCreatedAt: qrCodeData.createdAt
            } : null
        };

        try {
            setLoading(true);
            await saveToFirebase(receiptData);
            downloadReceipt(receiptData);
            toast.success('Receipt generated and saved successfully!');
            setFormData(prev => ({
                ...prev,
                volunteerName: '',
                paymentMethod: 'Cash'
            }));
            setDenominations(denominations.map(d => ({ ...d, count: 0 })));
        } catch (error) {
            toast.error('Error saving receipt. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const downloadReceipt = (receiptData) => {
        const receiptHTML = generatePDFOptimizedHTML(receiptData);
        const pdfWindow = window.open('', '_blank');
        if (!pdfWindow) {
            toast.error('Popup blocked. Please allow popups for this site to download the receipt.');
            return;
        }
        pdfWindow.document.write(receiptHTML);
        pdfWindow.document.close();
        pdfWindow.onload = () => {
            setTimeout(() => {
                pdfWindow.focus();
                pdfWindow.print();
            }, 1000);
        };
        toast.success('Receipt opened for PDF download. Select "Save as PDF" in the print dialog.');
    };

    const generatePDFOptimizedHTML = (data) => {
        const denominationRows = data.denominations.map(d =>
            `<tr>
                <td>₹ ${d.value}</td>
                <td>${d.count}</td>
                <td>₹ ${d.value * d.count}</td>
            </tr>`
        ).join('');

        return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Receipt-${data.receiptNumber}</title>
    <style>
        @page { size: A4; margin: 10mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; font-size: 11px; line-height: 1.2; color: #000; background: white; }
        .receipt-container { width: 100%; max-width: 190mm; margin: 0 auto; background: white; }
        .blue-header { height: 30px; background: #4472c4; width: 100%; margin-bottom: 15px; }
        .header-section { display: flex; justify-content: space-between; margin-bottom: 15px; font-size: 9px; }
        .header-left, .header-right { width: 30%; }
        .header-center { width: 40%; text-align: center; }
        .logo { width: 80px; height: 80px; margin: 0 auto 8px; display: flex; align-items: center; justify-content: center; position: relative; }
        .logo img { width: 80px; height: 80px; object-fit: contain; }
        .foundation-name { font-size: 12px; font-weight: bold; color: #4472c4; line-height: 1.1; }
        .receipt-info { display: flex; justify-content: space-between; margin: 15px 0; font-weight: bold; }
        .payment-section { display: flex; border: 1px solid #000; background: #f8f8f8; margin: 15px 0; }
        .payment-item { flex: 1; padding: 8px; text-align: center; border-right: 1px solid #000; font-weight: bold; }
        .payment-item:last-child { border-right: none; }
        .tables-section { display: flex; gap: 10mm; margin: 15px 0; }
        .table-container { flex: 1; }
        table { width: 100%; border-collapse: collapse; border: 1px solid #000; font-size: 10px; margin-bottom: 10px; }
        th { background: #e6e6e6; font-weight: bold; text-align: center; padding: 6px 4px; border: 1px solid #000; }
        td { padding: 4px 6px; border: 1px solid #000; vertical-align: top; }
        .donor-table td:first-child { font-weight: bold; background: #f8f8f8; width: 40%; }
        .denomination-table td { text-align: center; }
        .purpose-section, .contact-query, .address-section, .legal-section { margin: 10px 0; padding: 8px; border: 1px solid #000; background: #f8f8f8; text-align: center; }
        .donation-highlight { background: #d5e8d4; border: 1px solid #70ad47; padding: 10px; text-align: center; font-weight: bold; margin: 15px 0; }
        .contact-table { width: 100%; border: 1px solid #000; margin: 10px 0; }
        .contact-table td { text-align: center; padding: 8px 4px; font-weight: bold; font-size: 10px; }
        .legal-section { font-size: 8px; text-align: justify; line-height: 1.3; }
    </style>
</head>
<body>
    <div class="receipt-container">
        <div class="blue-header"></div>
        <div class="header-section">
            <div class="header-left">
                <strong>Section 12AB Reg.No:</strong><br>
                ${data.organizationDetails.regNo}<br><br>
                <strong>Corporate Identity No:</strong><br>
                ${data.organizationDetails.corporateId}
            </div>
            <div class="header-center">
                <div class="logo">
                    <img src='${LOGO_BASE64}' alt="Nimal Maula Foundation Logo" />
                </div>
                <div class="foundation-name">NIMAL MAULA<br>FOUNDATION</div>
            </div>
            <div class="header-right">
                <strong>Section-80G Registration No:</strong><br>
                ${data.organizationDetails.section80G}<br><br>
                <strong>Darpan ID No:</strong><br>
                ${data.organizationDetails.darpanId}
            </div>
        </div>
        <div class="receipt-info">
            <div>Volunteer Name: ${data.volunteerName}</div>
            <div>Receipt #${data.receiptNumber}</div>
        </div>
        <div class="payment-section">
            <div class="payment-item">Amount Paid<br>₹${data.totalAmount}</div>
            <div class="payment-item">Date & Time<br>${new Date(data.dateTime).toLocaleDateString('en-GB')} ${new Date(data.dateTime).toLocaleTimeString('en-US')}</div>
            <div class="payment-item">Payment Method<br>${data.paymentMethod}</div>
        </div>
        <div class="tables-section">
            <div class="table-container">
                <table class="donor-table">
                    <thead><tr><th>Donor details</th><th>Information</th></tr></thead>
                    <tbody>
                        <tr><td>Donor Name</td><td>${data.donorName}</td></tr>
                        <tr><td>Donor PAN</td><td>${data.donorPan || ''}</td></tr>
                        <tr><td>Email</td><td>${data.donorEmail || ''}</td></tr>
                        <tr><td>Mobile Number</td><td>${data.donorMobile || ''}</td></tr>
                        <tr><td>Address</td><td>${data.donorAddress || ''}</td></tr>
                    </tbody>
                </table>
            </div>
            <div class="table-container">
                <table class="denomination-table">
                    <thead><tr><th>Denomination (₹)</th><th>Count</th><th>Total (₹)</th></tr></thead>
                    <tbody>${denominationRows}</tbody>
                </table>
            </div>
        </div>
        <div class="purpose-section">
            Towards the pursuance of the main objects (${data.purpose}) of Nimal Maula Foundation.
        </div>
        <div class="donation-highlight">
            Your Donation: ₹${data.totalAmount} (${numberToWords(data.totalAmount)} Only)
        </div>
        <div class="contact-query">
            Any query related to this transaction, please contact Nimal Maula Foundation.
        </div>
        <table class="contact-table">
            <tr>
                <td>Email<br>${data.organizationDetails.email}</td>
                <td>Website<br>${data.organizationDetails.website}</td>
                <td>Contact<br>${data.organizationDetails.contact}</td>
            </tr>
        </table>
        <div class="address-section">
            ${data.organizationDetails.address}
        </div>
        <div class="legal-section">
            The donations to Nimal Maula Foundation are eligible for appropriate deduction(s) u/s 80G of the Income Tax Act, 1961. Nimal Maula Foundation further undertakes to confirm that it shall subsequently file its 'Annual Return of Donations' online on Form-10BD on or before the due date prescribed under the Income Tax Rules, 1962 which is 31st May following the end of financial year. Therefore, the amount(s) of donation(s) given today may be reflected in Donor' Annual Information Statement (AIS) by 30th June.
        </div>
    </div>
</body>
</html>`;
    };

    const numberToWords = (num) => {
        if (num === 0) return 'Zero';
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const thousands = ['', 'Thousand', 'Lakh', 'Crore'];
        function convertHundreds(n) {
            let result = '';
            if (n > 99) {
                result += ones[Math.floor(n / 100)] + ' Hundred ';
                n %= 100;
            }
            if (n > 19) {
                result += tens[Math.floor(n / 10)] + ' ';
                n %= 10;
            }
            if (n > 0) {
                result += ones[n] + ' ';
            }
            return result;
        }
        if (num < 1000) {
            return convertHundreds(num).trim();
        }
        let result = '';
        let place = 0;
        while (num > 0) {
            if (num % 1000 !== 0) {
                result = convertHundreds(num % 1000) + thousands[place] + ' ' + result;
            }
            num = Math.floor(num / 1000);
            place++;
        }
        return result.trim();
    };

    if (!isAuthenticated) {
        return (
            <Fragment>
                <Dialog
                    open={showPasswordDialog}
                    disableEscapeKeyDown
                    fullWidth
                    maxWidth="sm"
                    sx={{
                        '& .MuiDialog-paper': {
                            m: isMobile ? 2 : 3,
                            width: isMobile ? 'calc(100% - 32px)' : 'auto'
                        }
                    }}
                >
                    <DialogTitle sx={{
                        fontSize: isMobile ? '1.3rem' : '1.5rem',
                        textAlign: 'center',
                        pb: 1
                    }}>
                        Access Required
                    </DialogTitle>
                    <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
                        <Typography variant="body1" sx={{ mb: 3, fontSize: isMobile ? '16px' : '14px' }}>
                            Please enter the collection password to continue:
                        </Typography>
                        <TextField
                            autoFocus
                            fullWidth
                            type="password"
                            label="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                            size={isMobile ? "medium" : "medium"}
                            InputProps={{
                                style: { fontSize: isMobile ? '16px' : '14px' }
                            }}
                            InputLabelProps={{
                                style: { fontSize: isMobile ? '16px' : '14px' }
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{
                        px: isMobile ? 2 : 3,
                        pb: isMobile ? 2 : 2,
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? 1 : 0
                    }}>
                        <Button
                            onClick={() => navigate('/')}
                            sx={{
                                width: isMobile ? '100%' : 'auto',
                                minHeight: isMobile ? '48px' : '36px'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handlePasswordSubmit}
                            variant="contained"
                            color="primary"
                            sx={{
                                width: isMobile ? '100%' : 'auto',
                                minHeight: isMobile ? '48px' : '36px'
                            }}
                        >
                            Submit
                        </Button>
                    </DialogActions>
                </Dialog>
            </Fragment>
        );
    }

    if (loading && !formData.boxId) {
        return (
            <Fragment>
                <Navbar2 Logo={Logo} />
                <PageTitle pageTitle={'Loading...'} pagesub={'Please wait while we load the QR code data'} />
                <Container maxWidth="sm" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        <CircularProgress size={60} />
                        <Typography variant="body1" color="text.secondary">
                            Loading QR code data...
                        </Typography>
                    </Box>
                </Container>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Navbar2 Logo={Logo} />
            <PageTitle
                pageTitle={isMobile ? 'Collection' : 'Donation Collection'}
                pagesub={
                    formData.qrUUID
                        ? `QR: ${formData.qrUUID.slice(-8)} | ${formData.boxId}${isMobile ? '' : ` | ${formData.location}`}`
                        : `${formData.boxId}${isMobile ? '' : ` | ${formData.location}`}`
                }
            />
            <Box sx={{
                minHeight: '100vh',
                backgroundColor: isMobile ? '#f5f5f5' : 'inherit',
                pb: isMobile ? 2 : 0
            }}>
                <Container
                    maxWidth={isMobile ? "sm" : "lg"}
                    sx={{
                        px: isMobile ? 1 : 3,
                        py: isMobile ? 1 : 3
                    }}
                >
                    <Paper
                        elevation={isMobile ? 1 : 3}
                        sx={{
                            p: isMobile ? 2 : 4,
                            borderRadius: isMobile ? 3 : 1,
                            backgroundColor: '#fff'
                        }}
                    >
                        <Typography
                            variant={isMobile ? "h5" : "h4"}
                            sx={{
                                mb: isMobile ? 2 : 4,
                                textAlign: 'center',
                                fontWeight: isMobile ? 600 : 'normal',
                                fontSize: isSmallMobile ? '1.3rem' : 'inherit'
                            }}
                        >
                            Collection Form
                        </Typography>
                        {qrCodeData && (
                            <Alert
                                severity="info"
                                sx={{
                                    mb: isMobile ? 2 : 4,
                                    '& .MuiAlert-message': {
                                        fontSize: isMobile ? '14px' : 'inherit'
                                    }
                                }}
                            >
                                <Typography variant="body2">
                                    <strong>QR Code Data Loaded:</strong> Collection for {qrCodeData.donorName}
                                    {qrCodeData.donorMobile && ` (${qrCodeData.donorMobile})`}
                                    {!isMobile && qrCodeData.createdAt &&
                                        ` - Generated: ${new Date(qrCodeData.createdAt.seconds * 1000).toLocaleDateString()}`
                                    }
                                </Typography>
                            </Alert>
                        )}
                        <Stack spacing={isMobile ? 3 : 4}>
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: 2,
                                        color: '#4a90e2',
                                        fontSize: isMobile ? '1.1rem' : '1.25rem',
                                        fontWeight: 600
                                    }}
                                >
                                    Collection Information
                                </Typography>
                                <Grid container spacing={isMobile ? 2 : 3}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Box ID"
                                            name="boxId"
                                            value={formData.boxId}
                                            onChange={handleInputChange}
                                            disabled={!!qrCodeData}
                                            variant="outlined"
                                            placeholder="e.g., BOX001"
                                            InputProps={{
                                                style: {
                                                    fontSize: isMobile ? '16px' : '14px',
                                                    backgroundColor: qrCodeData ? '#f9f9f9' : 'inherit'
                                                }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Location"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleInputChange}
                                            disabled={!!qrCodeData}
                                            variant="outlined"
                                            placeholder="e.g., Main Entrance"
                                            InputProps={{
                                                style: {
                                                    fontSize: isMobile ? '16px' : '14px',
                                                    backgroundColor: qrCodeData ? '#f9f9f9' : 'inherit'
                                                }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Purpose"
                                            name="purpose"
                                            value={formData.purpose}
                                            onChange={handleInputChange}
                                            disabled={!!qrCodeData}
                                            variant="outlined"
                                            placeholder="e.g., Sadaqah"
                                            InputProps={{
                                                style: {
                                                    fontSize: isMobile ? '16px' : '14px',
                                                    backgroundColor: qrCodeData ? '#f9f9f9' : 'inherit'
                                                }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                {!qrCodeData && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                            <strong>Manual Entry:</strong> Please fill in all collection and donor details below.
                                        </Typography>
                                    </Alert>
                                )}
                            </Box>
                            <Divider />
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: 2,
                                        color: '#4a90e2',
                                        fontSize: isMobile ? '1.1rem' : '1.25rem',
                                        fontWeight: 600
                                    }}
                                >
                                    Collection Details
                                </Typography>
                                <Grid container spacing={isMobile ? 2 : 3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Volunteer Name *"
                                            name="volunteerName"
                                            value={formData.volunteerName}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            required
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        >
                                            {volunteers.map((volunteer) => (
                                                <MenuItem key={volunteer} value={volunteer}>
                                                    {volunteer}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Payment Method"
                                            name="paymentMethod"
                                            value={formData.paymentMethod}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        >
                                            <MenuItem value="Cash">Cash</MenuItem>
                                            <MenuItem value="Online">Online</MenuItem>
                                            <MenuItem value="Cheque">Cheque</MenuItem>
                                        </TextField>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: 2,
                                        color: '#4a90e2',
                                        fontSize: isMobile ? '1.1rem' : '1.25rem',
                                        fontWeight: 600
                                    }}
                                >
                                    Donor Information
                                    {qrCodeData && (
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontSize: isMobile ? '12px' : '14px',
                                                color: '#666',
                                                fontWeight: 'normal'
                                            }}
                                        >
                                            (Pre-filled from QR Code)
                                        </Typography>
                                    )}
                                </Typography>
                                <Grid container spacing={isMobile ? 2 : 3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Donor Name *"
                                            name="donorName"
                                            value={formData.donorName}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            required
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Mobile Number"
                                            name="donorMobile"
                                            value={formData.donorMobile}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="PAN Number"
                                            name="donorPan"
                                            value={formData.donorPan}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="donorEmail"
                                            value={formData.donorEmail}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Address"
                                            name="donorAddress"
                                            value={formData.donorAddress}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            multiline
                                            rows={isMobile ? 2 : 3}
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: 2,
                                        color: '#4a90e2',
                                        fontSize: isMobile ? '1.1rem' : '1.25rem',
                                        fontWeight: 600
                                    }}
                                >
                                    Denomination Count
                                </Typography>
                                {isMobile ? (
                                    <Box>
                                        {denominations.map((denom, index) => (
                                            <MobileDenominationCard
                                                key={denom.value}
                                                denom={denom}
                                                index={index}
                                                onCountChange={handleDenominationChange}
                                            />
                                        ))}
                                        <Card
                                            sx={{
                                                mt: 2,
                                                backgroundColor: '#e8f5e8',
                                                border: '2px solid #4caf50'
                                            }}
                                        >
                                            <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                                                        Total Amount
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Typography variant="body1" sx={{ fontWeight: 'bold', minWidth: '60px', textAlign: 'center' }}>
                                                            {denominations.reduce((sum, d) => sum + d.count, 0)} notes
                                                        </Typography>
                                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4caf50', minWidth: '100px', textAlign: 'right' }}>
                                                            ₹{calculateTotal()}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Box>
                                ) : (
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                                                    <TableCell>Denomination (₹)</TableCell>
                                                    <TableCell align="center">Count</TableCell>
                                                    <TableCell align="right">Total (₹)</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {denominations.map((denom, index) => (
                                                    <TableRow key={denom.value}>
                                                        <TableCell>₹ {denom.value}</TableCell>
                                                        <TableCell align="center">
                                                            <TextField
                                                                type="text"
                                                                value={denom.count === 0 ? '' : String(denom.count)}
                                                                onChange={(e) => {
                                                                    const sanitized = e.target.value.replace(/\D/g, '');
                                                                    handleDenominationChange(index, sanitized === '' ? 0 : parseInt(sanitized, 10));
                                                                }}
                                                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                                size="small"
                                                                style={{ width: '80px' }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            ₹ {denom.value * denom.count}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow style={{ backgroundColor: '#e8f5e8' }}>
                                                    <TableCell><strong>Total Amount</strong></TableCell>
                                                    <TableCell align="center">
                                                        <strong>{denominations.reduce((sum, d) => sum + d.count, 0)}</strong>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <strong>₹ {calculateTotal()}</strong>
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                            <Box sx={{ textAlign: 'center', pt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    onClick={generateReceipt}
                                    disabled={loading || calculateTotal() === 0 || !formData.volunteerName || !formData.donorName}
                                    sx={{
                                        minHeight: isMobile ? '56px' : '48px',
                                        fontSize: isMobile ? '18px' : '16px',
                                        fontWeight: 600,
                                        width: isMobile ? '100%' : 'auto',
                                        minWidth: isMobile ? 'auto' : '250px',
                                        borderRadius: isMobile ? 3 : 1,
                                        boxShadow: isMobile ? '0 4px 12px rgba(68, 114, 196, 0.3)' : 'inherit',
                                        textTransform: 'none'
                                    }}
                                >
                                    {loading ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <CircularProgress size={24} color="inherit" />
                                            <span>Generating...</span>
                                        </Box>
                                    ) : (
                                        'Generate PDF Receipt'
                                    )}
                                </Button>
                            </Box>
                        </Stack>
                    </Paper>
                </Container>
            </Box>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default DonationCollection;
 